/**
 * Buy One Get One — same product — storefront auto-doubler.
 *
 * For the promo collection (configured via the shop metafield
 * `custom.b1g1_collection`), every paid item is "doubled": a matching free
 * duplicate line (same variant, same quantity) is auto-added to the cart and
 * flagged with the line-item property `_b1g1 = true`. The duplicate's quantity
 * is kept in sync with the paid quantity and removed when the paid line goes.
 *
 * The 100% discount on the duplicate is applied server-side by the b1g1
 * product-discount function; this file only manages cart membership/quantities.
 *
 * Config comes from the <b1g1-doubler> element rendered by cart-b1g1.liquid:
 *   - data-collection-handle  the on-promo collection handle
 *
 * Collection membership is resolved by fetching the collection's product ids
 * from `/collections/{handle}/products.json` (paginated, cached per page load),
 * then matching each cart item's product_id.
 */
(function () {
  // Module-level lock so only one add/remove/update runs at a time across the
  // element being destroyed + recreated on cart re-renders.
  let b1g1Processing = false;
  // Set when an evaluation is requested while another is in flight, so it is
  // retried once the current pass finishes instead of being dropped.
  let b1g1ReevaluatePending = false;

  const B1G1_PROPERTY = "_b1g1";

  // Cache the collection's product-id set per page load, keyed by handle.
  const productIdCache = new Map();

  async function fetchCollectionProductIds(handle) {
    if (productIdCache.has(handle)) return productIdCache.get(handle);

    const ids = new Set();
    try {
      for (let page = 1; page <= 40; page++) {
        const res = await fetch(
          `${window.Shopify?.routes?.root || "/"}collections/${handle}/products.json?limit=250&page=${page}`,
        );
        if (!res.ok) break;
        const data = await res.json();
        const products = data.products || [];
        for (const p of products) ids.add(p.id);
        if (products.length < 250) break;
      }
    } catch (e) {
      /* network error → empty set; nothing gets doubled this pass */
    }

    productIdCache.set(handle, ids);
    return ids;
  }

  class B1G1Doubler extends HTMLElement {
    connectedCallback() {
      this.collectionHandle = this.dataset.collectionHandle || "";
      if (!this.collectionHandle) return;

      // Evaluate on connect (covers full page loads & section re-renders).
      this.evaluate();

      // Re-evaluate whenever any cart change is published.
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.evaluate();
      });
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    }

    async evaluate() {
      // Serialise evaluations behind a single lock with a drain loop, so the
      // final cart state always settles no matter how many cart events fire
      // while we're mid-flight — including when this element is destroyed and
      // recreated during a cart re-render (every quantity change on /cart).
      if (b1g1Processing) {
        b1g1ReevaluatePending = true;
        return;
      }
      b1g1Processing = true;
      try {
        do {
          b1g1ReevaluatePending = false;
          await this.reconcile();
        } while (b1g1ReevaluatePending);
      } finally {
        b1g1Processing = false;
      }
    }

    async reconcile() {
      const productIds = await fetchCollectionProductIds(this.collectionHandle);
      if (!productIds || productIds.size === 0) return;

      let cart;
      try {
        cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
      } catch (e) {
        return;
      }

      const items = cart.items || [];

      // Eligible paid quantity per variant (items in the promo collection that
      // are NOT the free duplicate).
      const paidByVariant = new Map();
      // Existing free duplicates per variant.
      const freeByVariant = new Map();

      for (const item of items) {
        if (this.isB1g1(item)) {
          freeByVariant.set(item.variant_id, { key: item.key, quantity: item.quantity });
          continue;
        }
        if (productIds.has(item.product_id)) {
          paidByVariant.set(
            item.variant_id,
            (paidByVariant.get(item.variant_id) || 0) + item.quantity,
          );
        }
      }

      // 1) Add or correct duplicates for each eligible paid variant.
      for (const [variantId, paidQty] of paidByVariant) {
        const free = freeByVariant.get(variantId);
        if (!free) {
          await this.addDuplicate(variantId, paidQty);
          return; // re-render → re-evaluate handles the rest
        }
        if (free.quantity !== paidQty) {
          await this.setQty(free.key, paidQty);
          return;
        }
      }

      // 2) Remove orphaned duplicates (paid line gone / no longer eligible).
      for (const [variantId, free] of freeByVariant) {
        if (!paidByVariant.has(variantId)) {
          await this.setQty(free.key, 0);
          return;
        }
      }
    }

    isB1g1(item) {
      return !!(item.properties && item.properties[B1G1_PROPERTY] === "true");
    }

    async addDuplicate(variantId, quantity) {
      const res = await fetch(`${routes.cart_add_url}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: quantity,
              properties: { [B1G1_PROPERTY]: "true" },
            },
          ],
        }),
      }).catch(() => null);
      if (res && res.ok) this.notifyCartChanged();
    }

    async setQty(key, quantity) {
      const res = await fetch(`${routes.cart_change_url}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity: quantity }),
      }).catch(() => null);
      if (res && res.ok) this.notifyCartChanged();
    }

    notifyCartChanged() {
      // Refresh the drawer + cart bubble without opening the drawer.
      document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
      // Refresh the cart page line items / other cartUpdate subscribers. This
      // re-enters evaluate() via our own subscriber; the in-flight lock turns it
      // into a queued drain pass, so the cart converges without an infinite loop.
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "b1g1-doubler" });
    }
  }

  if (!customElements.get("b1g1-doubler")) {
    customElements.define("b1g1-doubler", B1G1Doubler);
  }
})();
